import { ArrowUpRight } from "lucide-react";
import { Eyebrow, FadeUp, MaskLine } from "@/components/Reveal";
import { CONTACT } from "@/data/content";

const CHANNELS = [
  { label: "Email", value: CONTACT.email, url: `mailto:${CONTACT.email}`, testid: "contact-email-link" },
  { label: "Instagram", value: CONTACT.instagram.label, url: CONTACT.instagram.url, testid: "contact-instagram-link" },
  { label: "SoundCloud", value: CONTACT.soundcloud.label, url: CONTACT.soundcloud.url, testid: "contact-soundcloud-link" },
];

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <section className="relative overflow-hidden pt-40 md:pt-48" aria-label="Contact">
        <div
          className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-aubergine/50 blur-[130px]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <Eyebrow index="03">Contact</Eyebrow>
          <h1 className="mt-10 font-clash font-semibold uppercase leading-[0.95] tracking-tight">
            <MaskLine delay={0.1}>
              <span className="block text-[clamp(3rem,10vw,8rem)]">Parlons</span>
            </MaskLine>
            <MaskLine delay={0.22}>
              <span className="block text-[clamp(3rem,10vw,8rem)]">
                <span className="text-outline-ice">nous</span>
                <span className="text-glow">.</span>
              </span>
            </MaskLine>
          </h1>
          <FadeUp delay={0.35} className="mt-8 max-w-md">
            <p className="text-base leading-relaxed text-muted sm:text-lg">
              Booking, programmation, presse, collaboration — ou simple curiosité. Trois canaux,
              rien de plus.
            </p>
          </FadeUp>

          <div className="mt-16 pb-24">
            {CHANNELS.map((channel, i) => (
              <FadeUp key={channel.label} delay={0.1 + i * 0.07}>
                <a
                  href={channel.url}
                  target={channel.url.startsWith("http") ? "_blank" : undefined}
                  rel={channel.url.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={channel.testid}
                  className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-4 border-t border-line px-2 py-7 transition-colors duration-500 last:border-b hover:bg-teal/25 sm:gap-8 sm:py-9"
                >
                  <span className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-[0.3em] text-ice sm:w-32">
                    {channel.label}
                  </span>
                  <span className="break-all font-clash text-xl font-medium tracking-tight text-fog transition-colors duration-300 group-hover:text-glow sm:text-3xl md:text-4xl">
                    {channel.value}
                  </span>
                  <ArrowUpRight
                    className="h-5 w-5 shrink-0 text-muted transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-glow sm:h-7 sm:w-7"
                    aria-hidden="true"
                  />
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-[1400px] px-5 pb-28 sm:px-8"
        aria-label="Mentions légales"
      >
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <figure className="relative -rotate-1 border border-line bg-night p-2 shadow-[14px_14px_0_0_#0E2B2A]">
              <img
                src="/assets/crowd-pink.jpg"
                alt="Foule sous des lumières roses et violettes lors d'un événement Chroma"
                className="aspect-[16/9] w-full object-cover"
                loading="lazy"
                width="1080"
                height="720"
              />
              <figcaption className="absolute -bottom-4 left-5 border border-line bg-night px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
                Chroma — Lyon, France
              </figcaption>
            </figure>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-4 lg:col-start-9">
            <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-ice">
              <span className="h-2 w-2 bg-glow" aria-hidden="true" />
              Mentions légales
            </p>
            <address
              data-testid="legal-info"
              className="mt-6 space-y-2 font-mono text-sm not-italic leading-relaxed text-muted"
            >
              <p className="text-fog">{CONTACT.legal.name}</p>
              <p>{CONTACT.legal.address}</p>
              <p>RNA {CONTACT.legal.rna}</p>
              <p>SIRET {CONTACT.legal.siret}</p>
            </address>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
